import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trpc } from "@/lib/trpc";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, XCircle, Bell } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";

export default function Generator() {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [keywords, setKeywords] = useState("");
  const [results, setResults] = useState<any[]>([]);

  const [isGenerating, setIsGenerating] = useState(false);
  const trpcUtils = trpc.useUtils();
  
  const handleGenerate = async () => {
    if (!keywords.trim()) {
      toast.error("Please enter at least one keyword");
      return;
    }
    
    setIsGenerating(true);
    try {
      const keywordArray = keywords.split(",").map(k => k.trim()).filter(Boolean);
      const data = await trpcUtils.client.domain.generate.query({ keywords: keywordArray });
      setResults(data);
    } catch (error: any) {
      toast.error(error.message || "Failed to generate domains");
    } finally {
      setIsGenerating(false);
    }
  };

  const addMonitor = trpc.monitor.add.useMutation({
    onSuccess: () => {
      toast.success(t.monitor.addSuccess);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });



  const handleAddToMonitor = (domain: string) => {
    if (!isAuthenticated) {
      toast.error("Please login to use monitoring feature");
      return;
    }

    // Extract TLD from domain
    const parts = domain.split(".");
    const tld = parts.length > 1 ? `.${parts[parts.length - 1]}` : ".com";
    const domainName = parts.slice(0, -1).join(".");

    addMonitor.mutate({ domain: domainName, tld });
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      prefix: "bg-blue-500/20 text-blue-500",
      suffix: "bg-purple-500/20 text-purple-500",
      compound: "bg-orange-500/20 text-orange-500",
      creative: "bg-pink-500/20 text-pink-500",
    };
    return (
      <Badge className={colors[category] || "bg-gray-500/20 text-gray-500"}>
        {category}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            {t.generator.title}
          </h1>
          <p className="text-muted-foreground">{t.generator.subtitle}</p>
        </div>

        {/* Generator Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t.generator.generate}</CardTitle>
            <CardDescription>
              {t.generator.keywordPlaceholder}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Input
                placeholder={t.generator.keywordPlaceholder}
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                className="flex-1"
              />
              <Button onClick={handleGenerate} disabled={isGenerating} className="w-full sm:w-auto">
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t.generator.generating}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t.generator.generate}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {t.generator.noResults}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((result, index) => (
              <Card key={index} className={result.available ? "border-green-500/50" : "border-border"}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="font-mono font-bold text-lg mb-2 break-all">
                        {result.domain}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {getCategoryBadge(result.category)}
                        {result.available ? (
                          <Badge className="bg-green-500/20 text-green-500">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {t.generator.available}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            {t.generator.taken}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {result.available && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleAddToMonitor(result.domain)}
                      disabled={!isAuthenticated || addMonitor.isPending}
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      {t.generator.addToMonitor}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
